import api from './api'

interface RegisterData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  citizenship?: string;
}

export const authService = {
  // Login
  async login(email: string, password: string) {
    const response = await api.post('/auth/login/', {
      email,
      password,
    })
    
    const { user, tokens } = response.data
    localStorage.setItem('access_token', tokens.access)
    localStorage.setItem('refresh_token', tokens.refresh)
    if (user?.role) localStorage.setItem('user_role', user.role)
    return user
  },

  // Register - creates account and triggers email verification
  async register(userData: RegisterData) {
    const response = await api.post('/auth/register/', userData)
    return response.data
  },

  // Logout
  async logout() {
    const refresh = localStorage.getItem('refresh_token')
    if (refresh) {
      try {
        await api.post('/auth/logout/', { refresh })
      } catch (error) {
        console.error('Logout error:', error)
      }
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_role')
  },

  // Get current user
  async getCurrentUser() {
    const response = await api.get('/auth/me/')
    return response.data
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('access_token')
  },

  // Password reset request
  async requestPasswordReset(email: string, contactId?: string) {
    const payload: any = { email };
    if (contactId) {
      payload.contact_id = contactId;
    }
    const response = await api.post('/auth/password-reset/', payload)
    return response.data
  },

  // Password reset - verify code (step 2: validates code without setting password)
  async verifyResetCode(email: string, code: string) {
    const response = await api.post('/auth/password-reset/verify/', {
      email,
      code: code.toUpperCase(),
    })
    return response.data
  },

  // Password reset confirm (step 3: validates code and sets new password)
  async resetPassword(
    email: string, 
    code: string, 
    newPassword: string,
    legacyMigrationData?: {
      contactId?: string;
      firstName?: string;
      lastName?: string;
    }
  ) {
    const payload: any = {
      email,
      code: code.toUpperCase(),
      new_password: newPassword,
    };
    
    // Include legacy migration data if provided
    if (legacyMigrationData?.contactId) {
      payload.contact_id = legacyMigrationData.contactId;
      payload.first_name = legacyMigrationData.firstName || '';
      payload.last_name = legacyMigrationData.lastName || '';
    }
    
    const response = await api.post('/auth/password-reset/confirm/', payload)
    
    const { tokens, user } = response.data
    if (tokens) {
      localStorage.setItem('access_token', tokens.access)
      localStorage.setItem('refresh_token', tokens.refresh)
    }
    
    return { ...response.data, user }
  },

  // Verify email code (OTP verification after registration)
  async verifyCode(email: string, code: string) {
    const response = await api.post('/auth/verify-code/', {
      email,
      code: code.toUpperCase(),
    })
    
    const { tokens, user } = response.data
    if (tokens) {
      localStorage.setItem('access_token', tokens.access)
      localStorage.setItem('refresh_token', tokens.refresh)
    }
    
    return { ...response.data, user }
  },

  // Resend verification code
  async resendVerificationCode(email: string) {
    const response = await api.post('/auth/verify-email/resend/', { email })
    return response.data
  },

  async agencyLogin(email: string, password: string) {
    const response = await api.post('/agency/login/', { email, password })
    const { user, tokens } = response.data
    localStorage.setItem('access_token', tokens.access)
    localStorage.setItem('refresh_token', tokens.refresh)
    if (user?.role) localStorage.setItem('user_role', user.role)
    return user
  },

  async agencyVerifyCode(email: string, code: string) {
    const response = await api.post('/agency/verify-code/', {
      email,
      code: code.toUpperCase(),
    })
    const { tokens, user } = response.data
    if (tokens) {
      localStorage.setItem('access_token', tokens.access)
      localStorage.setItem('refresh_token', tokens.refresh)
    }
    if (user?.role) localStorage.setItem('user_role', user.role)
    return { ...response.data, user }
  },

  async agencyResendVerificationCode(email: string) {
    const response = await api.post('/agency/verify-code/resend/', { email })
    return response.data
  },

  async agencyRegister(data: {
    company_name: string;
    tax_number?: string;
    website?: string;
    address_country?: string;
    address_city?: string;
    address_street?: string;
    address_zip?: string;
    first_name: string;
    last_name: string;
    preferred_contact_method?: string;
    phone_code: string;
    phone_number: string;
    email: string;
    password: string;
  }) {
    const response = await api.post('/agency/register/', data)
    return response.data
  },

  // Agency password reset — step 1: request OTP
  async agencyRequestPasswordReset(email: string) {
    const response = await api.post('/agency/password-reset/', { email })
    return response.data
  },

  // Agency password reset — step 2: verify OTP (without setting password)
  async agencyVerifyResetCode(email: string, code: string) {
    const response = await api.post('/agency/password-reset/verify/', {
      email,
      code: code.toUpperCase(),
    })
    return response.data
  },

  // Agency password reset — step 3: set new password
  async agencyResetPassword(email: string, code: string, newPassword: string) {
    const response = await api.post('/agency/password-reset/confirm/', {
      email,
      code: code.toUpperCase(),
      new_password: newPassword,
    })
    const { tokens, user } = response.data
    if (tokens) {
      localStorage.setItem('access_token', tokens.access)
      localStorage.setItem('refresh_token', tokens.refresh)
      if (user?.role) localStorage.setItem('user_role', user.role)
    }
    return { ...response.data, user }
  },

  // Change password (authenticated user)
  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
    const response = await api.post('/auth/change-password/', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    })
    return response.data
  },
}
